import React, { useState, useEffect } from 'react';
import { REPORT_TYPES } from './DashboardLayout';
import { useAuth } from '../context/AuthContext';
import { API_BASE } from '../config';

// ─── Helper to map report type names to IDs ──────────────────────────────────
function getReportTypeId(typeName) {
  if (!typeName) return 'wgs';
  const norm = String(typeName).toLowerCase();
  if (norm.includes('bacterial') || norm.includes('bac_wgs') || norm.includes('bacterial_wgs')) return 'bacterial_wgs';
  if (norm.includes('shotgun') || norm.includes('meta')) return 'meta';
  if (norm.includes('mrna')) return 'mRNA';
  if (norm.includes('16s') || norm.includes('v1')) return '16sv1-v9';
  if (norm.includes('wes')) return 'wes';
  if (norm.includes('wgs')) return 'wgs';
  return 'wgs';
}

// ─── Shared pill ──────────────────────────────────────────────────────────────
function Pill({ type, label }) {
  const map = {
    pass:      { bg: 'rgba(77,217,172,0.12)', color: '#4dd9ac', border: 'rgba(77,217,172,0.3)' },
    generated: { bg: 'rgba(77,217,172,0.12)', color: '#4dd9ac', border: 'rgba(77,217,172,0.3)' },
    warn:      { bg: 'rgba(240,192,64,0.12)', color: '#f0c040', border: 'rgba(240,192,64,0.3)' },
    fail:      { bg: 'rgba(240,96,96,0.12)',  color: '#f06060', border: 'rgba(240,96,96,0.3)' },
  };
  const c = map[type?.toLowerCase()] || map.generated || map.pass;
  return (
    <span style={{
      display: 'inline-block', padding: '3px 9px', borderRadius: 4,
      fontSize: 11, fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase',
      fontFamily: "'IBM Plex Mono', monospace",
      background: c.bg, color: c.color, border: `0.5px solid ${c.border}`,
    }}>{label || type}</span>
  );
}

// ─── Panel wrapper ────────────────────────────────────────────────────────────
function Panel({ t, title, accentColor, children, style, rightHeader }) {
  return (
    <div style={{
      background: t.cardBg,
      border: `1px solid ${t.cardBorder}`,
      borderRadius: 8,
      padding: '16px 18px',
      boxShadow: t.dark ? '0 4px 20px rgba(0,0,0,0.25)' : '0 2px 10px rgba(0,0,0,0.04)',
      display: 'flex',
      flexDirection: 'column',
      ...style,
    }}>
      <div style={{
        fontFamily: "'Barlow Condensed', sans-serif", fontSize: 16, fontWeight: 600,
        textTransform: 'uppercase', letterSpacing: '0.1em',
        color: t.textSub, marginBottom: 14,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ width: 8, height: 8, borderRadius: '50%', background: accentColor || '#00b4d8', display: 'inline-block', flexShrink: 0 }} />
          {title}
        </div>
        {rightHeader}
      </div>
      {children}
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────
export default function DashboardHome({ t, dark, reportReadyMap: parentReportReadyMap = {}, onNavigate }) {
  const authContext = useAuth();
  const authFetch = authContext?.authFetch;
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [hoveredCard, setHoveredCard] = useState(null);

  const fetchStats = async () => {
    try {
      setIsRefreshing(true);
      let res;
      if (authFetch) {
        try {
          res = await authFetch(`${API_BASE}/api/dashboard/stats`);
        } catch (_) {}
      }
      if (!res || !res.ok) {
        res = await fetch(`${API_BASE}/api/dashboard/stats`);
      }
      if (res.ok) {
        const data = await res.json();
        if (data && data.success) {
          setStats(data);
        }
      }
    } catch (err) {
      console.error('Failed to fetch dashboard stats:', err);
    } finally {
      setLoading(false);
      setIsRefreshing(false);
    }
  };

  useEffect(() => {
    fetchStats();
    const handleFocus = () => fetchStats();
    window.addEventListener('focus', handleFocus);
    const interval = setInterval(fetchStats, 15000);
    return () => {
      window.removeEventListener('focus', handleFocus);
      clearInterval(interval);
    };
  }, [authFetch]);

  const reportReadyMap = stats?.reportReadyMap || parentReportReadyMap;
  const totalGenerated  = stats ? stats.totalGenerated : Object.values(reportReadyMap).filter(Boolean).length;
  const totalTypes      = stats ? stats.totalTypes : REPORT_TYPES.length;
  const pendingTypes    = stats ? stats.pendingTypes : (totalTypes - totalGenerated);
  const totalReports    = stats ? stats.totalReports : 0;
  const todayCount      = stats ? (stats.todayCount || 0) : 0;
  const thisMonthCount  = stats ? stats.thisMonthCount : 0;
  const samplesProcessed= stats ? stats.samplesProcessed : 0;

  const recentActivity  = stats?.recentReports || [];

  const typeUsage       = stats?.reportsByType || [
    { label: 'WGS',                 count: 0, pct: 0, color: '#00b4d8' },
    { label: 'Bacterial WGS',       count: 0, pct: 0, color: '#0d9488' },
    { label: '16S (V1–V9)',          count: 0, pct: 0, color: '#10b981' },
    { label: 'mRNA',                count: 0, pct: 0, color: '#6366f1' },
    { label: 'Shotgun Metagenomics', count: 0, pct: 0, color: '#f43f5e' },
    { label: 'WES',                 count: 0, pct: 0, color: '#a855f7' },
  ];

  const kpiCardStyle = (accentColor, keyName) => {
    const isHovered = hoveredCard === keyName;
    return {
      background: t.cardBg,
      border: `1px solid ${isHovered ? accentColor : t.cardBorder}`,
      borderRadius: 8,
      padding: '16px 18px',
      position: 'relative',
      overflow: 'hidden',
      cursor: 'default',
      transition: 'all 0.22s cubic-bezier(0.4, 0, 0.2, 1)',
      transform: isHovered ? 'translateY(-2px)' : 'none',
      boxShadow: isHovered
        ? (dark ? `0 6px 20px ${accentColor}20` : `0 6px 16px ${accentColor}18`)
        : (dark ? '0 4px 16px rgba(0,0,0,0.2)' : '0 2px 8px rgba(0,0,0,0.04)'),
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'space-between',
    };
  };

  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      gap: 20,
      padding: '20px 24px',
      minHeight: '100%',
      boxSizing: 'border-box',
      overflowY: 'auto',
    }}>

      {/* ── KPI cards row (Fluid Responsive Grid) ───────────────────────── */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
        gap: 16,
      }}>

        {/* Card 1: Reports Generated */}
        <div
          style={kpiCardStyle('#00b4d8', 'kpi-generated')}
          onMouseEnter={() => setHoveredCard('kpi-generated')}
          onMouseLeave={() => setHoveredCard(null)}
        >
          <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 3, background: '#00b4d8' }} />
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
            <span style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.1em', color: t.textSub, fontWeight: 500 }}>
              Reports Generated
            </span>
          </div>
          <div style={{ fontFamily: "'Barlow Condensed', sans-serif", fontSize: 48, fontWeight: 700, lineHeight: 1, color: '#00b4d8' }}>
            {loading ? '...' : totalReports.toLocaleString()}
          </div>
          <div style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 11, color: t.textSub, marginTop: 8 }}>
            Total generated so far {todayCount > 0 ? `(+${todayCount} today)` : ''}
          </div>
        </div>

        {/* Card 2: Active Pipelines */}
        <div
          style={kpiCardStyle('#4dd9ac', 'kpi-total')}
          onMouseEnter={() => setHoveredCard('kpi-total')}
          onMouseLeave={() => setHoveredCard(null)}
        >
          <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 3, background: '#4dd9ac' }} />
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
            <span style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.1em', color: t.textSub, fontWeight: 500 }}>
              Active Pipelines
            </span>
          </div>
          <div style={{ fontFamily: "'Barlow Condensed', sans-serif", fontSize: 48, fontWeight: 700, lineHeight: 1, color: '#4dd9ac' }}>
            {String(totalGenerated).padStart(2, '0')}
            <span style={{ fontSize: 18, fontWeight: 400, color: t.textSub, marginLeft: 6 }}>/ {totalTypes}</span>
          </div>
          <div style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 11, color: t.textSub, marginTop: 8 }}>
            {pendingTypes === 0 ? 'All 6 pipelines active' : `${pendingTypes} type${pendingTypes !== 1 ? 's' : ''} pending`}
          </div>
        </div>

        {/* Card 3: Samples Processed */}
        <div
          style={kpiCardStyle('#f0c040', 'kpi-samples')}
          onMouseEnter={() => setHoveredCard('kpi-samples')}
          onMouseLeave={() => setHoveredCard(null)}
        >
          <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 3, background: '#f0c040' }} />
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
            <span style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.1em', color: t.textSub, fontWeight: 500 }}>
              Samples Processed
            </span>
          </div>
          <div style={{ fontFamily: "'Barlow Condensed', sans-serif", fontSize: 48, fontWeight: 700, lineHeight: 1, color: '#f0c040' }}>
            {loading ? '...' : samplesProcessed.toLocaleString()}
          </div>
          <div style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 11, color: t.textSub, marginTop: 8 }}>
            Across all pipelines ({thisMonthCount > 0 ? `+${thisMonthCount} this month` : 'All time'})
          </div>
        </div>

      </div>

      {/* ── Main Content (Recent Reports + Reports by Type) ───────────── */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(360px, 1fr))',
        gap: 16,
      }}>

        {/* Recent Reports */}
        <Panel
          t={t}
          title="Recent Reports"
          accentColor="#4dd9ac"
          rightHeader={
            <button
              type="button"
              onClick={fetchStats}
              disabled={isRefreshing}
              style={{
                background: 'transparent',
                border: `1px solid ${t.cardBorder}`,
                borderRadius: 4,
                padding: '3px 8px',
                color: t.textSub,
                fontSize: 11,
                fontFamily: "'IBM Plex Mono', monospace",
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: 5,
                transition: 'all 0.15s ease',
              }}
              title="Refresh recent reports"
            >
              <span style={{ display: 'inline-block', transform: isRefreshing ? 'rotate(360deg)' : 'none', transition: 'transform 0.6s' }}>↻</span>
              {isRefreshing ? 'Syncing…' : 'Refresh'}
            </button>
          }
        >
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontFamily: "'IBM Plex Mono', monospace", fontSize: 12 }}>
              <thead>
                <tr>{['Type', 'Project', 'Date', 'Samples', 'Status'].map(h => (
                  <th key={h} style={{
                    textAlign: 'left', padding: '0 8px 8px 0',
                    color: t.textSub, fontWeight: 500, fontSize: 11,
                    letterSpacing: '0.08em', textTransform: 'uppercase',
                    borderBottom: `1px solid ${t.cardBorder}`,
                  }}>{h}</th>
                ))}</tr>
              </thead>
              <tbody>
                {loading && !stats && (
                  <tr>
                    <td colSpan="5" style={{ textAlign: 'center', padding: '24px 0', color: t.textSub }}>
                      Loading recent reports…
                    </td>
                  </tr>
                )}
                {!loading && recentActivity.length === 0 && (
                  <tr>
                    <td colSpan="5" style={{ textAlign: 'center', padding: '28px 0', color: t.textSub, fontStyle: 'italic' }}>
                      No reports generated yet. Select a report type from the sidebar to upload files.
                    </td>
                  </tr>
                )}
                {recentActivity.map((r, i) => {
                  const typeId = r.typeId || getReportTypeId(r.type);
                  const isReady = reportReadyMap[typeId];
                  return (
                    <tr
                      key={r.id || i}
                      style={{ cursor: 'pointer', transition: 'background 0.15s ease' }}
                      onMouseEnter={e => e.currentTarget.style.background = dark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)'}
                      onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                      onClick={() => {
                        if (r.url) {
                          const fullUrl = r.url?.startsWith('http') ? r.url : `${API_BASE}${r.url}`;
                          window.open(fullUrl, '_blank');
                        }
                        if (onNavigate) {
                          onNavigate(typeId, isReady ? 'report' : 'upload');
                        }
                      }}
                      title={`Click to open ${r.project} (${r.type})`}
                    >
                      <td style={{ padding: '9px 8px 9px 0', borderBottom: `1px solid ${t.cardBorder}`, color: t.text, fontWeight: 600 }}>{r.type}</td>
                      <td style={{ padding: '9px 8px 9px 0', borderBottom: `1px solid ${t.cardBorder}`, color: t.textSub, maxWidth: 130, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.project}</td>
                      <td style={{ padding: '9px 8px 9px 0', borderBottom: `1px solid ${t.cardBorder}`, color: t.textSub }}>{r.date}</td>
                      <td style={{ padding: '9px 8px 9px 0', borderBottom: `1px solid ${t.cardBorder}`, color: t.text }}>{r.samples}</td>
                      <td style={{ padding: '9px 0', borderBottom: `1px solid ${t.cardBorder}` }}>
                        <Pill
                          type={r.status === 'pass' ? 'generated' : r.status}
                          label={r.status === 'pass' ? 'GENERATED' : (r.status?.toUpperCase() || 'GENERATED')}
                        />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Panel>

        {/* Reports by Type */}
        <Panel t={t} title="Reports by Type" accentColor="#a855f7">
          {typeUsage.map(u => {
            const typeId = getReportTypeId(u.label);
            const rt = REPORT_TYPES.find(r => r.id === typeId);
            const itemColor = rt?.accent || u.color || '#00b4d8';
            const ready = reportReadyMap[typeId];
            return (
              <div
                key={u.label}
                onClick={() => onNavigate && onNavigate(typeId, ready ? 'report' : 'upload')}
                style={{ marginBottom: 12, cursor: 'pointer' }}
                title={`Click to open ${u.label}`}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 5, fontFamily: "'IBM Plex Mono', monospace", fontSize: 12 }}>
                  <span style={{ color: t.textSub, display: 'flex', alignItems: 'center', gap: 6 }}>
                    <span style={{ width: 6, height: 6, borderRadius: '50%', background: itemColor, display: 'inline-block' }} />
                    {u.label}
                  </span>
                  <span style={{ color: t.text, fontFamily: "'Barlow Condensed', sans-serif", fontSize: 18, fontWeight: 700, minWidth: 28, textAlign: 'right' }}>{u.count}</span>
                </div>
                <div style={{ height: 6, background: dark ? '#1e3460' : '#e0eaf4', borderRadius: 3, overflow: 'hidden' }}>
                  <div style={{ width: `${u.pct}%`, height: '100%', background: itemColor, borderRadius: 3, transition: 'width 0.4s ease' }} />
                </div>
              </div>
            );
          })}
        </Panel>

      </div>
    </div>
  );
}